import React, { useState } from 'react';
import './HRInsights.css';

// ── small helpers ───────────────────────────────────────────────────────────────────────────────
const TONE_CLASS = { bad: 'hi-dot-bad', warn: 'hi-dot-warn', good: 'hi-dot-good', info: 'hi-dot-info' };
const STATUS_LABEL = { ok: 'OK', info: 'Info', warn: 'Review', bad: 'Problem', unknown: 'Unknown' };

function timeOf(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true });
}

// ── WHAT STANDS OUT ─────────────────────────────────────────────────────────────────────────────
export function InsightsCard({ data }) {
  const list = data?.insights || [];
  return (
    <div className="ov-card hi-card">
      <div className="ov-card-header">
        <div>
          <div className="ov-card-title">What stands out</div>
          <div className="ov-card-sub">Worked out from today's records. No AI, nothing to wait for.</div>
        </div>
      </div>
      {list.length === 0 ? (
        <div className="hi-empty">Nothing unusual right now.</div>
      ) : (
        <ul className="hi-insights">
          {list.map((it, i) => (
            <li key={i} className="hi-insight">
              <span className={`hi-dot ${TONE_CLASS[it.tone] || 'hi-dot-info'}`} />
              <div>
                <div className="hi-insight-text">{it.text}</div>
                {it.names && it.names.length > 0 && (
                  <div className="hi-insight-names">{it.names.join(' · ')}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── LATE WATCH ──────────────────────────────────────────────────────────────────────────────────
function Squares({ lates }) {
  const MAX = 15;
  const total = Math.min(MAX, Math.max(9, Math.ceil(lates / 3) * 3 + 3));
  const cells = [];
  for (let i = 1; i <= total; i++) {
    let cls = 'hi-sq';
    if (i <= lates) cls += i % 3 === 0 ? ' hi-sq-red' : ' hi-sq-amber';
    cells.push(<span key={i} className={cls} />);
  }
  return (
    <span className="hi-squares">
      {cells}
      {lates > MAX && <span className="hi-sq-more">+{lates - MAX}</span>}
    </span>
  );
}

function LatePill({ p }) {
  if (p.state === 'one_away') return <span className="hi-pill hi-pill-bad">1 late away</span>;
  if (p.state === 'charged') {
    return <span className="hi-pill hi-pill-warn">charged {p.deduction_days} day{p.deduction_days === 1 ? '' : 's'}</span>;
  }
  return <span className="hi-pill hi-pill-soft">{p.away} away</span>;
}

export function LateWatchCard({ data }) {
  const lw = data?.late_watch;
  const people = lw?.people || [];
  return (
    <div className="ov-card hi-card">
      <div className="ov-card-header">
        <div>
          <div className="ov-card-title">Late watch · {lw?.month || ''}</div>
          <div className="ov-card-sub">{lw?.rule || 'Who is close to the next late deduction.'}</div>
        </div>
        {lw && (
          <div className="hi-chips">
            <span className="hi-chip hi-chip-bad">{lw.summary.one_away} one away</span>
            <span className="hi-chip hi-chip-warn">{lw.summary.reached} charged</span>
          </div>
        )}
      </div>

      {!lw ? (
        <div className="hi-empty">Loading…</div>
      ) : people.length === 0 ? (
        <div className="hi-empty">Nobody has 2 or more lates this month.</div>
      ) : (
        <div className="hi-late-list">
          {people.map((p) => (
            <div key={p.id} className="hi-late-row">
              <div className="hi-late-who">
                <div className="hi-late-name">{p.name}</div>
                <div className="hi-late-meta">
                  {p.city}
                  {p.casual_left !== null && p.casual_left !== undefined ? ` · casual left ${p.casual_left}` : ''}
                  {p.last_month > 0 ? ` · last month ${p.last_month}` : ''}
                </div>
              </div>
              <div className="hi-late-squares">
                <Squares lates={p.lates} />
                <span className="hi-late-count">{p.lates}{p.late_today > 0 ? ' · late today' : ''}</span>
              </div>
              <LatePill p={p} />
            </div>
          ))}
        </div>
      )}

      <div className="hi-legend">
        <span className="hi-sq hi-sq-amber" /> one late
        <span className="hi-sq hi-sq-red" /> completes a group of three
        {lw && lw.summary.with_lates > people.length && (
          <span className="hi-legend-more"> · {lw.summary.with_lates - people.length} more with a single late</span>
        )}
      </div>
    </div>
  );
}

// ── SYSTEM HEALTH ───────────────────────────────────────────────────────────────────────────────
function CheckRow({ c, forceOpen }) {
  const hasMore = (c.items && c.items.length > 0) || c.action;
  const [openState, setOpen] = useState(c.status === 'bad' || c.status === 'warn');
  const open = forceOpen || openState;
  return (
    <div className={`hi-check hi-check-${c.status}`}>
      <div
        className={`hi-check-head ${hasMore ? 'hi-clickable' : ''}`}
        onClick={() => hasMore && setOpen(!openState)}
        role={hasMore ? 'button' : undefined}
        tabIndex={hasMore ? 0 : undefined}
        onKeyDown={(e) => { if (hasMore && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(!openState); } }}
      >
        <span className={`hi-status-dot hi-st-${c.status}`} title={STATUS_LABEL[c.status]} />
        <div className="hi-check-main">
          <div className="hi-check-title">{c.title}</div>
          {c.detail && <div className="hi-check-detail">{c.detail}</div>}
        </div>
        <div className="hi-check-right">
          <span className={`hi-value hi-val-${c.status}`}>{c.value}</span>
          {hasMore && <span className="hi-caret">{open ? '▾' : '▸'}</span>}
        </div>
      </div>
      {open && hasMore && (
        <div className="hi-check-body">
          {c.items && c.items.length > 0 && (
            <div className="hi-items">
              {c.items.map((it, i) => (
                <div key={i} className="hi-item">
                  <span className="hi-item-label">{it.label}</span>
                  <span className="hi-item-value">{it.value}</span>
                </div>
              ))}
            </div>
          )}
          {c.action && <div className="hi-action"><b>What to do:</b> {c.action}</div>}
        </div>
      )}
    </div>
  );
}

function Group({ g, forceOpen }) {
  const issues = g.checks.filter((c) => c.status === 'bad' || c.status === 'warn' || c.status === 'unknown').length;
  const [open, setOpen] = useState(issues > 0);
  const isOpen = forceOpen || open;
  return (
    <div className="hi-group">
      <div className="hi-group-head" onClick={() => setOpen(!open)} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(!open); } }}>
        <span className={`hi-status-dot hi-st-${g.status}`} />
        <span className="hi-group-title">{g.title}</span>
        <span className="hi-group-count">
          {g.checks.length} checks{issues > 0 ? ` · ${issues} to look at` : ' · all fine'}
        </span>
        <span className="hi-caret">{isOpen ? '▾' : '▸'}</span>
      </div>
      {isOpen && (
        <div className="hi-group-body">
          {g.checks.map((c) => <CheckRow key={c.id} c={c} forceOpen={forceOpen} />)}
        </div>
      )}
    </div>
  );
}

export function SystemHealthCard({ data, onRefresh, refreshing, error }) {
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const h = data?.health;

  let groups = h?.groups || [];
  if (onlyIssues) {
    groups = groups
      .map((g) => ({ ...g, checks: g.checks.filter((c) => c.status === 'bad' || c.status === 'warn' || c.status === 'unknown') }))
      .filter((g) => g.checks.length);
  }

  const s = h?.summary;
  const overall = !s ? null : s.bad > 0 ? 'bad' : (s.warn + s.unknown) > 0 ? 'warn' : 'ok';
  const overallText = !s ? '' : s.bad > 0
    ? `${s.bad} problem${s.bad > 1 ? 's' : ''}${s.warn ? `, ${s.warn} to review` : ''}`
    : (s.warn + s.unknown) > 0 ? `${s.warn + s.unknown} to review` : 'Everything is healthy';

  return (
    <div className="ov-card hi-card hi-health">
      <div className="ov-card-header">
        <div>
          <div className="ov-card-title">
            System and data health
            {overall && <span className={`hi-overall hi-overall-${overall}`}>{overallText}</span>}
          </div>
          <div className="ov-card-sub">
            {s ? `${s.total} checks across server, database, scheduled jobs, data quality, face kiosk and security` : 'Checking…'}
            {data?.generated_at ? ` · checked ${timeOf(data.generated_at)}` : ''}
          </div>
        </div>
        <div className="hi-health-tools">
          <label className="hi-toggle">
            <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
            Only problems
          </label>
          <button className="hi-btn" onClick={() => setExpandAll(!expandAll)}>{expandAll ? 'Collapse all' : 'Expand all'}</button>
          <button className="hi-btn hi-btn-primary" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? 'Checking…' : '↻ Check now'}
          </button>
        </div>
      </div>

      {error && !h && <div className="hi-error">Could not load health checks: {error}</div>}

      {s && (
        <div className="hi-summary">
          <span className="hi-sum hi-sum-ok">{s.ok} healthy</span>
          <span className="hi-sum hi-sum-info">{s.info} for information</span>
          <span className="hi-sum hi-sum-warn">{s.warn + s.unknown} to review</span>
          <span className="hi-sum hi-sum-bad">{s.bad} problems</span>
        </div>
      )}

      {groups.length === 0 && h && <div className="hi-empty">No problems found.</div>}
      {groups.map((g) => <Group key={g.id + (onlyIssues ? '-i' : '')} g={g} forceOpen={expandAll} />)}
    </div>
  );
}
